import React, { useCallback, useEffect, useState } from 'react';
import api from '../services/api';

/* ─── Tipos ─── */
interface Visita {
  id: number;
  id_fila: number | null;
  responsavel_nome: string;
  responsavel_telefone: string;
  responsavel_email: string | null;
  crianca_nome: string | null;
  crianca_nascimento: string | null;
  turma_pretendida: string | null;
  id_turma_pretendida: number | null;
  turno_pretendido: string | null;
  canal: string;
  data_hora: string;
  status: string;
  resultado: string;
  proximo_contato_em: string | null;
  observacoes: string | null;
}
interface Interacao {
  id: number;
  canal: string;
  data_hora: string;
  anotacao: string;
}
interface FollowUpVencido {
  id: number;
  responsavel_nome: string;
  responsavel_telefone: string;
  crianca_nome: string | null;
  proximo_contato_em: string;
  dias_atraso: number;
}
interface Painel {
  total: number;
  agendadas_hoje: number;
  agendadas_proximos_7_dias: number;
  realizadas: number;
  nao_compareceram: number;
  taxa_comparecimento: number | null;
  matriculados: number;
  taxa_conversao: number | null;
  follow_ups_vencidos: FollowUpVencido[];
  por_canal: Array<{ canal: string; total: number }>;
}
interface Turma {
  id_turma: number;
  nome: string;
}

const EMERALD = '#10b981';
const card: React.CSSProperties = {
  background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14,
  padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,.06)',
};
const btn = (bg: string): React.CSSProperties => ({
  background: bg, color: '#fff', border: 'none', borderRadius: 8,
  padding: '8px 14px', fontWeight: 600, cursor: 'pointer', fontSize: 13,
});
const tag = (bg: string, color: string): React.CSSProperties => ({
  background: bg, color, padding: '2px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700,
});
const input: React.CSSProperties = {
  width: '100%', padding: '9px 11px', border: '1px solid #cbd5e1',
  borderRadius: 8, fontSize: 14, boxSizing: 'border-box',
};
const rotulo: React.CSSProperties = { fontSize: 12, fontWeight: 700, color: '#475569', display: 'block', marginBottom: 4 };

const CANAIS: Array<{ valor: string; texto: string }> = [
  { valor: 'indicacao', texto: 'Indicação' },
  { valor: 'instagram', texto: 'Instagram' },
  { valor: 'facebook', texto: 'Facebook' },
  { valor: 'google', texto: 'Google' },
  { valor: 'site', texto: 'Site da creche' },
  { valor: 'fachada', texto: 'Passou na porta' },
  { valor: 'whatsapp', texto: 'WhatsApp' },
  { valor: 'outro', texto: 'Outro' },
];
const STATUS: Array<{ valor: string; texto: string; cor: string; fundo: string }> = [
  { valor: 'agendada', texto: 'Agendada', cor: '#1d4ed8', fundo: '#dbeafe' },
  { valor: 'realizada', texto: 'Realizada', cor: '#065f46', fundo: '#d1fae5' },
  { valor: 'nao_compareceu', texto: 'Não compareceu', cor: '#92400e', fundo: '#fef3c7' },
  { valor: 'cancelada', texto: 'Cancelada', cor: '#475569', fundo: '#f1f5f9' },
];
const RESULTADOS: Array<{ valor: string; texto: string }> = [
  { valor: 'sem_definicao', texto: 'Sem definição' },
  { valor: 'interessado', texto: 'Interessado' },
  { valor: 'indeciso', texto: 'Indeciso' },
  { valor: 'desistiu', texto: 'Desistiu' },
  { valor: 'matriculado', texto: 'Matriculado' },
];
const CANAIS_CONTATO = ['telefone', 'whatsapp', 'email', 'presencial', 'outro'];

const rotuloDe = (lista: Array<{ valor: string; texto: string }>, valor: string) =>
  lista.find((i) => i.valor === valor)?.texto || valor;

function fmtDataHora(iso?: string | null) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}
/** Data pura (YYYY-MM-DD) sem passar por fuso — ver PR #82. */
function fmtData(valor?: string | null) {
  if (!valor) return '—';
  const [ano, mes, dia] = String(valor).slice(0, 10).split('-');
  return ano && mes && dia ? `${dia}/${mes}/${ano}` : '—';
}
/** `datetime-local` exige "YYYY-MM-DDTHH:mm" em hora local. */
function paraInputDataHora(iso?: string | null) {
  const d = iso ? new Date(iso) : new Date();
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const VAZIA = {
  responsavel_nome: '', responsavel_telefone: '', responsavel_email: '',
  crianca_nome: '', crianca_nascimento: '', turma_pretendida: '', id_turma_pretendida: '',
  turno_pretendido: '', canal: 'indicacao', data_hora: '', status: 'agendada',
  resultado: 'sem_definicao', proximo_contato_em: '', observacoes: '',
};

export default function VisitasPage() {
  const [visitas, setVisitas] = useState<Visita[]>([]);
  const [painel, setPainel] = useState<Painel | null>(null);
  const [turmas, setTurmas] = useState<Turma[]>([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState<{ tipo: 'ok' | 'erro'; texto: string } | null>(null);
  const [filtroStatus, setFiltroStatus] = useState('');

  const [form, setForm] = useState<any | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [followUp, setFollowUp] = useState<{ visita: Visita; itens: Interacao[] } | null>(null);
  const [novoContato, setNovoContato] = useState({ canal: 'telefone', anotacao: '', proximo_contato_em: '' });

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const [vi, pa, tu] = await Promise.all([
        api.get('/academico/visitas', { params: filtroStatus ? { status: filtroStatus } : {} }),
        api.get('/academico/visitas/painel'),
        api.get('/turmas/ocupacao').catch(() => ({ data: [] })),
      ]);
      setVisitas(Array.isArray(vi.data) ? vi.data : []);
      setPainel(pa.data || null);
      setTurmas(Array.isArray(tu.data) ? tu.data : []);
    } catch {
      setMsg({ tipo: 'erro', texto: 'Falha ao carregar as visitas.' });
    } finally {
      setLoading(false);
    }
  }, [filtroStatus]);

  useEffect(() => { carregar(); }, [carregar]);

  async function acao(fn: () => Promise<any>, sucesso: string) {
    try {
      await fn();
      setMsg({ tipo: 'ok', texto: sucesso });
      await carregar();
    } catch (e: any) {
      setMsg({ tipo: 'erro', texto: e?.response?.data?.message || 'Operação falhou.' });
    }
  }

  function abrirNova() {
    setForm({ ...VAZIA, data_hora: paraInputDataHora(null) });
  }

  function abrirEdicao(v: Visita) {
    setForm({
      id: v.id,
      responsavel_nome: v.responsavel_nome || '',
      responsavel_telefone: v.responsavel_telefone || '',
      responsavel_email: v.responsavel_email || '',
      crianca_nome: v.crianca_nome || '',
      crianca_nascimento: (v.crianca_nascimento || '').slice(0, 10),
      turma_pretendida: v.turma_pretendida || '',
      id_turma_pretendida: v.id_turma_pretendida ? String(v.id_turma_pretendida) : '',
      turno_pretendido: v.turno_pretendido || '',
      canal: v.canal || 'outro',
      data_hora: paraInputDataHora(v.data_hora),
      status: v.status,
      resultado: v.resultado,
      proximo_contato_em: (v.proximo_contato_em || '').slice(0, 10),
      observacoes: v.observacoes || '',
    });
  }

  async function salvar() {
    if (!form) return;
    if (!form.responsavel_nome.trim() || !form.responsavel_telefone.trim()) {
      setMsg({ tipo: 'erro', texto: 'Nome e telefone do responsável são obrigatórios.' });
      return;
    }
    if (!form.data_hora) {
      setMsg({ tipo: 'erro', texto: 'Informe a data e a hora da visita.' });
      return;
    }
    setSalvando(true);
    const corpo = {
      ...form,
      id_turma_pretendida: form.id_turma_pretendida ? Number(form.id_turma_pretendida) : null,
      crianca_nascimento: form.crianca_nascimento || null,
      proximo_contato_em: form.proximo_contato_em || null,
    };
    delete corpo.id;
    try {
      if (form.id) await api.put(`/academico/visitas/${form.id}`, corpo);
      else await api.post('/academico/visitas', corpo);
      setForm(null);
      setMsg({ tipo: 'ok', texto: form.id ? 'Visita atualizada.' : 'Visita agendada.' });
      await carregar();
    } catch (e: any) {
      setMsg({ tipo: 'erro', texto: e?.response?.data?.message || 'Não foi possível salvar a visita.' });
    } finally {
      setSalvando(false);
    }
  }

  async function abrirFollowUp(v: Visita) {
    try {
      const { data } = await api.get<Interacao[]>(`/academico/visitas/${v.id}/interacoes`);
      setNovoContato({ canal: 'telefone', anotacao: '', proximo_contato_em: (v.proximo_contato_em || '').slice(0, 10) });
      setFollowUp({ visita: v, itens: Array.isArray(data) ? data : [] });
    } catch {
      setMsg({ tipo: 'erro', texto: 'Não foi possível abrir o histórico de contatos.' });
    }
  }

  async function registrarContato() {
    if (!followUp) return;
    if (!novoContato.anotacao.trim()) {
      setMsg({ tipo: 'erro', texto: 'Escreva o que foi tratado no contato.' });
      return;
    }
    try {
      await api.post(`/academico/visitas/${followUp.visita.id}/interacoes`, {
        canal: novoContato.canal,
        anotacao: novoContato.anotacao.trim(),
        proximo_contato_em: novoContato.proximo_contato_em || null,
      });
      const { data } = await api.get<Interacao[]>(`/academico/visitas/${followUp.visita.id}/interacoes`);
      setFollowUp({ ...followUp, itens: Array.isArray(data) ? data : [] });
      setNovoContato((p) => ({ ...p, anotacao: '' }));
      setMsg({ tipo: 'ok', texto: 'Contato registrado.' });
      await carregar();
    } catch (e: any) {
      setMsg({ tipo: 'erro', texto: e?.response?.data?.message || 'Não foi possível registrar o contato.' });
    }
  }

  async function paraFila(v: Visita) {
    let nome = v.crianca_nome || '';
    if (!nome) {
      nome = window.prompt('Nome da criança para inscrever na fila de espera:') || '';
      if (!nome.trim()) return;
    }
    await acao(
      () => api.post(`/academico/visitas/${v.id}/para-fila`, { aluno_nome: nome.trim() }),
      'Inscrito na fila de espera — siga em Demanda & Matrículas.',
    );
  }

  if (loading) return <div style={{ padding: 24 }}>Carregando…</div>;

  const nomeTurma = new Map(turmas.map((t) => [t.id_turma, t.nome]));
  const estiloStatus = (valor: string) => {
    const s = STATUS.find((i) => i.valor === valor);
    return tag(s?.fundo || '#f1f5f9', s?.cor || '#475569');
  };

  return (
    <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, color: '#0f172a' }}>Visitas & Captação</h1>
          <p style={{ margin: '4px 0 0', color: '#64748b' }}>
            Agende a visita dos pais, acompanhe o retorno e inscreva na fila de espera sem redigitar nada.
          </p>
        </div>
        <button style={btn(EMERALD)} onClick={abrirNova}>+ Agendar visita</button>
      </div>

      {msg && (
        <div style={{
          ...card, padding: '12px 16px',
          borderLeft: `4px solid ${msg.tipo === 'ok' ? EMERALD : '#ef4444'}`,
          color: msg.tipo === 'ok' ? '#065f46' : '#991b1b',
        }}>
          {msg.texto}
        </div>
      )}

      {/* ── Painel de captação ── */}
      {painel && (
        <section style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 14 }}>
          {[
            { rotulo: 'Visitas hoje', valor: painel.agendadas_hoje, cor: '#2563eb' },
            { rotulo: 'Próximos 7 dias', valor: painel.agendadas_proximos_7_dias, cor: '#0ea5e9' },
            { rotulo: 'Realizadas', valor: painel.realizadas, cor: EMERALD },
            {
              rotulo: 'Comparecimento',
              valor: painel.taxa_comparecimento === null ? '—' : `${painel.taxa_comparecimento}%`,
              cor: '#8b5cf6',
            },
            {
              rotulo: 'Conversão em matrícula',
              valor: painel.taxa_conversao === null ? '—' : `${painel.taxa_conversao}%`,
              cor: '#f59e0b',
            },
          ].map((kpi) => (
            <div key={kpi.rotulo} style={{ ...card, borderTop: `3px solid ${kpi.cor}` }}>
              <div style={{ fontSize: 12, color: '#64748b', fontWeight: 700 }}>{kpi.rotulo}</div>
              <div style={{ fontSize: 26, fontWeight: 800, color: '#0f172a', marginTop: 4 }}>{kpi.valor}</div>
            </div>
          ))}
        </section>
      )}

      {/* ── Follow-ups vencidos: a lista que faz a creche não perder o lead ── */}
      {painel && painel.follow_ups_vencidos.length > 0 && (
        <section style={{ ...card, borderLeft: '4px solid #f97316' }}>
          <h2 style={{ margin: '0 0 10px', fontSize: 16, color: '#0f172a' }}>
            ⏰ Retornos combinados e ainda não feitos ({painel.follow_ups_vencidos.length})
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {painel.follow_ups_vencidos.map((f) => {
              const visita = visitas.find((v) => v.id === f.id);
              return (
                <div key={f.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 14 }}>
                    <b>{f.responsavel_nome}</b>
                    {f.crianca_nome ? ` — ${f.crianca_nome}` : ''}
                    <span style={{ color: '#64748b' }}> · {f.responsavel_telefone}</span>
                    <span style={{ ...tag('#fff7ed', '#c2410c'), marginLeft: 8 }}>
                      {f.dias_atraso === 0 ? 'para hoje' : `${f.dias_atraso}d de atraso`}
                    </span>
                  </div>
                  {visita && (
                    <button style={btn('#f97316')} onClick={() => abrirFollowUp(visita)}>Registrar contato</button>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ── Agenda ── */}
      <section>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, gap: 12, flexWrap: 'wrap' }}>
          <h2 style={{ fontSize: 18, color: '#0f172a', margin: 0 }}>Agenda de visitas</h2>
          <select value={filtroStatus} onChange={(e) => setFiltroStatus(e.target.value)} style={{ ...input, width: 200 }}>
            <option value="">Todos os status</option>
            {STATUS.map((s) => <option key={s.valor} value={s.valor}>{s.texto}</option>)}
          </select>
        </div>

        <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                <th style={{ padding: 12 }}>Quando</th>
                <th style={{ padding: 12 }}>Responsável</th>
                <th style={{ padding: 12 }}>Criança</th>
                <th style={{ padding: 12 }}>Canal</th>
                <th style={{ padding: 12 }}>Status</th>
                <th style={{ padding: 12 }}>Resultado</th>
                <th style={{ padding: 12 }}>Retorno</th>
                <th style={{ padding: 12 }}></th>
              </tr>
            </thead>
            <tbody>
              {visitas.length === 0 ? (
                <tr><td colSpan={8} style={{ padding: 16, color: '#64748b' }}>Nenhuma visita registrada.</td></tr>
              ) : visitas.map((v) => (
                <tr key={v.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                  <td style={{ padding: 12, whiteSpace: 'nowrap' }}>{fmtDataHora(v.data_hora)}</td>
                  <td style={{ padding: 12 }}>
                    {v.responsavel_nome}
                    <div style={{ fontSize: 12, color: '#64748b' }}>{v.responsavel_telefone}</div>
                  </td>
                  <td style={{ padding: 12 }}>
                    {v.crianca_nome || '—'}
                    <div style={{ fontSize: 12, color: '#64748b' }}>
                      {(v.id_turma_pretendida && nomeTurma.get(v.id_turma_pretendida)) || v.turma_pretendida || ''}
                    </div>
                  </td>
                  <td style={{ padding: 12 }}>{rotuloDe(CANAIS, v.canal)}</td>
                  <td style={{ padding: 12 }}><span style={estiloStatus(v.status)}>{rotuloDe(STATUS, v.status)}</span></td>
                  <td style={{ padding: 12 }}>
                    {v.resultado === 'sem_definicao' ? <span style={{ color: '#94a3b8' }}>—</span> : rotuloDe(RESULTADOS, v.resultado)}
                    {v.id_fila && <span style={{ ...tag('#ecfdf5', '#047857'), marginLeft: 6 }}>na fila</span>}
                  </td>
                  <td style={{ padding: 12, whiteSpace: 'nowrap' }}>{fmtData(v.proximo_contato_em)}</td>
                  <td style={{ padding: 12 }}>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                      {v.status === 'agendada' && (
                        <>
                          <button
                            style={btn(EMERALD)}
                            onClick={() => acao(
                              () => api.patch(`/academico/visitas/${v.id}/status`, { status: 'realizada', resultado: 'interessado' }),
                              'Visita marcada como realizada.',
                            )}
                          >
                            Compareceu
                          </button>
                          <button
                            style={btn('#f59e0b')}
                            onClick={() => acao(
                              () => api.patch(`/academico/visitas/${v.id}/status`, { status: 'nao_compareceu' }),
                              'Registrado como não compareceu.',
                            )}
                          >
                            Faltou
                          </button>
                        </>
                      )}
                      <button style={btn('#6366f1')} onClick={() => abrirFollowUp(v)}>Contatos</button>
                      {!v.id_fila && v.status !== 'cancelada' && (
                        <button style={btn('#0ea5e9')} onClick={() => paraFila(v)}>→ Fila</button>
                      )}
                      <button style={btn('#334155')} onClick={() => abrirEdicao(v)}>Editar</button>
                      <button
                        style={btn('#ef4444')}
                        onClick={() => {
                          if (window.confirm(`Excluir a visita de ${v.responsavel_nome}?`)) {
                            acao(() => api.delete(`/academico/visitas/${v.id}`), 'Visita excluída.');
                          }
                        }}
                      >
                        Excluir
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ── De onde vêm as famílias ── */}
      {painel && painel.por_canal.length > 0 && (
        <section style={card}>
          <h2 style={{ margin: '0 0 12px', fontSize: 16, color: '#0f172a' }}>De onde vêm as famílias</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {painel.por_canal.map((c) => {
              const pct = painel.total ? Math.round((c.total / painel.total) * 100) : 0;
              return (
                <div key={c.canal} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 140, fontSize: 13, color: '#475569' }}>{rotuloDe(CANAIS, c.canal)}</div>
                  <div style={{ flex: 1, background: '#f1f5f9', borderRadius: 999, height: 10 }}>
                    <div style={{ width: `${pct}%`, background: EMERALD, height: 10, borderRadius: 999 }} />
                  </div>
                  <div style={{ width: 70, fontSize: 13, color: '#0f172a', fontWeight: 700 }}>{c.total} ({pct}%)</div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ── Modal: agendar/editar ── */}
      {form && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}
          onClick={() => setForm(null)}
        >
          <div style={{ ...card, width: 720, maxWidth: '96vw', maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0, color: '#0f172a' }}>{form.id ? 'Editar visita' : 'Agendar visita'}</h3>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
              <div>
                <label style={rotulo}>Responsável *</label>
                <input style={input} value={form.responsavel_nome} onChange={(e) => setForm({ ...form, responsavel_nome: e.target.value })} />
              </div>
              <div>
                <label style={rotulo}>Telefone *</label>
                <input style={input} value={form.responsavel_telefone} onChange={(e) => setForm({ ...form, responsavel_telefone: e.target.value })} />
              </div>
              <div>
                <label style={rotulo}>E-mail</label>
                <input style={input} value={form.responsavel_email} onChange={(e) => setForm({ ...form, responsavel_email: e.target.value })} />
              </div>
              <div>
                <label style={rotulo}>Criança</label>
                <input style={input} value={form.crianca_nome} onChange={(e) => setForm({ ...form, crianca_nome: e.target.value })} />
              </div>
              <div>
                <label style={rotulo}>Nascimento da criança</label>
                <input type="date" style={input} value={form.crianca_nascimento} onChange={(e) => setForm({ ...form, crianca_nascimento: e.target.value })} />
              </div>
              <div>
                <label style={rotulo}>Data e hora da visita *</label>
                <input type="datetime-local" style={input} value={form.data_hora} onChange={(e) => setForm({ ...form, data_hora: e.target.value })} />
              </div>
              <div>
                <label style={rotulo}>Como chegou até nós</label>
                <select style={input} value={form.canal} onChange={(e) => setForm({ ...form, canal: e.target.value })}>
                  {CANAIS.map((c) => <option key={c.valor} value={c.valor}>{c.texto}</option>)}
                </select>
              </div>
              <div>
                <label style={rotulo}>Turma de interesse</label>
                <select style={input} value={form.id_turma_pretendida} onChange={(e) => setForm({ ...form, id_turma_pretendida: e.target.value })}>
                  <option value="">—</option>
                  {turmas.map((t) => <option key={t.id_turma} value={t.id_turma}>{t.nome}</option>)}
                </select>
              </div>
              <div>
                <label style={rotulo}>Turno</label>
                <select style={input} value={form.turno_pretendido} onChange={(e) => setForm({ ...form, turno_pretendido: e.target.value })}>
                  <option value="">—</option>
                  <option value="matutino">Matutino</option>
                  <option value="vespertino">Vespertino</option>
                  <option value="integral">Integral</option>
                </select>
              </div>
              {form.id && (
                <>
                  <div>
                    <label style={rotulo}>Status</label>
                    <select style={input} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                      {STATUS.map((s) => <option key={s.valor} value={s.valor}>{s.texto}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={rotulo}>Resultado</label>
                    <select style={input} value={form.resultado} onChange={(e) => setForm({ ...form, resultado: e.target.value })}>
                      {RESULTADOS.map((r) => <option key={r.valor} value={r.valor}>{r.texto}</option>)}
                    </select>
                  </div>
                </>
              )}
              <div>
                <label style={rotulo}>Retornar em</label>
                <input type="date" style={input} value={form.proximo_contato_em} onChange={(e) => setForm({ ...form, proximo_contato_em: e.target.value })} />
              </div>
            </div>

            <div style={{ marginTop: 12 }}>
              <label style={rotulo}>Observações</label>
              <textarea rows={3} style={{ ...input, resize: 'vertical' }} value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button style={btn('#94a3b8')} onClick={() => setForm(null)}>Cancelar</button>
              <button style={btn(EMERALD)} disabled={salvando} onClick={salvar}>
                {salvando ? 'Salvando…' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: follow-up ── */}
      {followUp && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}
          onClick={() => setFollowUp(null)}
        >
          <div style={{ ...card, width: 640, maxWidth: '96vw', maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0, color: '#0f172a' }}>
              Contatos — {followUp.visita.responsavel_nome}
            </h3>
            <p style={{ marginTop: 0, fontSize: 13, color: '#64748b' }}>
              {followUp.visita.responsavel_telefone}
              {followUp.visita.crianca_nome ? ` · ${followUp.visita.crianca_nome}` : ''}
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={rotulo}>Canal</label>
                <select style={input} value={novoContato.canal} onChange={(e) => setNovoContato({ ...novoContato, canal: e.target.value })}>
                  {CANAIS_CONTATO.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label style={rotulo}>Retornar em</label>
                <input type="date" style={input} value={novoContato.proximo_contato_em} onChange={(e) => setNovoContato({ ...novoContato, proximo_contato_em: e.target.value })} />
              </div>
            </div>
            <div style={{ marginTop: 12 }}>
              <label style={rotulo}>O que foi tratado</label>
              <textarea rows={3} style={{ ...input, resize: 'vertical' }} value={novoContato.anotacao} onChange={(e) => setNovoContato({ ...novoContato, anotacao: e.target.value })} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
              <button style={btn(EMERALD)} onClick={registrarContato}>Registrar contato</button>
            </div>

            <h4 style={{ margin: '20px 0 8px', fontSize: 14, color: '#0f172a' }}>Histórico</h4>
            {followUp.itens.length === 0 ? (
              <p style={{ color: '#64748b', fontSize: 13 }}>Nenhum contato registrado ainda.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {followUp.itens.map((i) => (
                  <div key={i.id} style={{ borderLeft: '3px solid #e2e8f0', paddingLeft: 10 }}>
                    <div style={{ fontSize: 12, color: '#64748b' }}>{fmtDataHora(i.data_hora)} · {i.canal}</div>
                    <div style={{ fontSize: 14, color: '#0f172a', whiteSpace: 'pre-wrap' }}>{i.anotacao}</div>
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <button style={btn('#334155')} onClick={() => setFollowUp(null)}>Fechar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
